/**
 * Meta Pixel standard `Lead` conversion.
 *
 * The pixel base code (init + PageView) is injected by utils/metaPixel.ts, so this
 * only fires the additional `Lead` event.
 *
 * Reliability strategy:
 * 1. Fire through `fbq` as soon as the page mounts, retrying until fbevents.js is up.
 * 2. Verify via Resource Timing that a request to facebook.com/tr with ev=Lead
 *    actually went out.
 * 3. If it did not, send Meta's image beacon directly as a fallback.
 * Session-guarded so a refresh cannot double count.
 */
import { META_PIXEL_ID } from './metaPixel'

const STORAGE_KEY = 'next_meta_lead_sent'
const PARAMS = {
  content_name: 'NEXT Agent Recruiting Form',
  content_category: 'Agent Recruiting',
}

interface PixelWindow extends Window {
  fbq?: (...args: unknown[]) => void
  nextFireLead?: () => void
}

function alreadySent(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function markSent(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, 'true')
  } catch {
    /* private mode: the in-page guard still applies */
  }
}

/** True when the browser has actually issued a facebook.com/tr request with ev=Lead. */
function leadRequestObserved(): boolean {
  try {
    return performance
      .getEntriesByType('resource')
      .some((entry) => /facebook\.com\/tr/.test(entry.name) && /ev=Lead/i.test(entry.name))
  } catch {
    return false
  }
}

/** Meta's documented image beacon. Used only when the fbq call produced no request. */
function sendImageBeacon(): void {
  const query = new URLSearchParams({
    id: META_PIXEL_ID,
    ev: 'Lead',
    dl: window.location.href,
    rl: document.referrer,
    if: 'false',
    ts: String(Date.now()),
    'cd[content_name]': PARAMS.content_name,
    'cd[content_category]': PARAMS.content_category,
    noscript: '1',
  })
  const img = new Image(1, 1)
  img.src = `https://www.facebook.com/tr?${query.toString()}`
  console.log('Meta Pixel Lead sent via image beacon fallback.')
}

let sentThisPageLoad = false

/** Fires `Lead` if the pixel is available. Returns true when the call was made. */
export function fireLeadNow(source: string): boolean {
  if (sentThisPageLoad || alreadySent()) return true

  const w = window as PixelWindow
  if (typeof w.fbq !== 'function') return false

  w.fbq('track', 'Lead', { ...PARAMS, source })
  sentThisPageLoad = true
  markSent()
  console.log('Meta Pixel Lead event fired.', { source })

  // Confirm the network request actually went out; otherwise use the beacon.
  window.setTimeout(() => {
    if (!leadRequestObserved()) sendImageBeacon()
  }, 1500)

  return true
}

/** Fires `Lead` on page view, retrying until fbevents.js has attached `fbq`. */
export function fireLeadOnPageView(source = 'thank_you_page'): () => void {
  let attempts = 0
  let timer: number | undefined
  let cancelled = false

  const attempt = () => {
    if (cancelled) return
    attempts += 1
    if (fireLeadNow(source)) return
    if (attempts < 25) {
      timer = window.setTimeout(attempt, 250)
    } else if (!alreadySent()) {
      // fbevents.js never loaded (blocked or failed): still record the lead.
      sentThisPageLoad = true
      markSent()
      sendImageBeacon()
    }
  }

  // Manual trigger for live debugging in DevTools.
  ;(window as PixelWindow).nextFireLead = () => {
    sentThisPageLoad = false
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      /* ignore */
    }
    if (!fireLeadNow('manual_debug')) sendImageBeacon()
  }

  attempt()

  return () => {
    cancelled = true
    window.clearTimeout(timer)
  }
}
